import { useFlag } from '@reflag/react-sdk';
import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';
import { getSiteSetupConversationPrefix } from '@services/content-agents/browser';

import { apiQuery } from '../../state';
import {
    type OrganizationAgentLocation,
    useOrganizationConversations,
    useSetAgentPendingPrompt,
} from '../Agent';
import type { AgentDockTab } from '../AgentDock';

/** Whether the setup guide is started, or continued once some of its steps are completed. */
export type AgentSiteSetupGuideStarter = 'start' | 'continue';

/**
 * The site's setup guide, offered by the welcome on the site until it is published. It continues
 * the latest setup conversation in the history rather than starting another one.
 *
 * @returns
 * - `guide`: the guide starting point ("start", "continue", or null).
 * - `start`: a function to start the setup guide which opens the setup conversation and asks the agent to go on with the guide.
 */
export function useSiteSetupGuideStarter(props: {
    organizationId: string;
    location: OrganizationAgentLocation;
    /** How the surface opens another conversation. Without it, there is no guide to offer. */
    onOpenConversation?: (tab: AgentDockTab) => void;
}): { guide: AgentSiteSetupGuideStarter | null | undefined; start: (prompt: string) => void } {
    const { organizationId, location, onOpenConversation } = props;
    const siteAISetupflag = useFlag('SITE_AI_SETUP');
    const siteId =
        siteAISetupflag.isEnabled && onOpenConversation && !location.spaceId
            ? location.siteId
            : undefined;

    const { data: siteSetupData, isError } = useQuery({
        ...apiQuery('getSiteSetup', siteId ? { organizationId, siteId } : skipToken),
        throwOnError: false,
    });
    const setupConversation = useSiteSetupConversation({ organizationId, siteId });

    const start = useEventCallback((prompt: string) => {
        if (!setupConversation || !onOpenConversation) {
            return;
        }
        setupConversation.sendPromptOnOpen(prompt);
        onOpenConversation({ kind: 'organization', conversationId: setupConversation.id });
    });

    const guide = (() => {
        if (siteAISetupflag.isLoading) {
            return undefined;
        }
        if (!siteId || isError) {
            return null;
        }
        if (!siteSetupData || !setupConversation) {
            return undefined;
        }
        if (siteSetupData.steps.length === 0) {
            return null;
        }
        return setupConversation.started || siteSetupData.steps.some((step) => step.completed)
            ? 'continue'
            : 'start';
    })();

    return { guide, start };
}

/**
 * The site's setup conversation: the latest one in the history, or a new one.
 * Undefined while the history loads, or without a site.
 *
 * @return
 * - `id`: the conversation ID.
 * - `started`: whether the conversation already exists.
 * - `sendPromptOnOpen`: hands the conversation a prompt, which it sends once it is opened.
 */
export function useSiteSetupConversation(props: {
    organizationId: string;
    siteId: string | undefined;
}): { id: string; started: boolean; sendPromptOnOpen: (prompt: string) => void } | undefined {
    const { organizationId, siteId } = props;
    const { conversations, isLoading } = useOrganizationConversations(organizationId, {
        siteId,
        skip: !siteId,
    });
    // Fixed for the component's life, so a link to the new conversation and the prompt it is
    // handed name the same one.
    const [newId] = React.useState(() => crypto.randomUUID());
    const setPendingPrompt = useSetAgentPendingPrompt();

    if (!siteId || isLoading) {
        return undefined;
    }

    const prefix = getSiteSetupConversationPrefix(siteId);
    const existing = conversations.find((item) => item.id.startsWith(prefix));
    const id = existing?.id ?? `${prefix}${newId}`;

    return {
        id,
        started: Boolean(existing),
        sendPromptOnOpen: (prompt) =>
            setPendingPrompt({ conversationId: id, prompt, submit: true }),
    };
}
